<script setup lang="ts">
import { computed, watch } from 'vue'
import { Field, useField } from 'vee-validate'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'
import { useI18n } from '@/composable/useI18n'
import DropdownCheckbox from '@/components/Globals/Inputs/DropdownCheckbox.vue'
import {
  buildSensorSchemaFieldOptions,
  buildSensorSchemaFieldOptionsFromKeys,
  type ISensorSchemaFieldOption,
  type ISensorWithSchema,
} from '@/utils/sensor-schema-fields'

export type WidgetDataFieldSelection = 'multi' | 'single-array' | 'single'

export type WidgetDataFieldSelectionMode = WidgetDataFieldSelection | 'empty' | 'status'

export type WidgetDataFieldStatusKind = 'loading' | 'unsupported' | 'error'

const props = withDefaults(
  defineProps<{
    hasSelectedSensor: boolean
    sensorDetails: ISensorWithSchema | null
    isLoadingDetails: boolean
    detailsError: boolean
    unsupportedSensorType: boolean
    selection?: WidgetDataFieldSelection
    alreadySelectedDataField?: string
    fieldOptions?: ISensorSchemaFieldOption[]
    fieldLabelClass?: string
    fieldInputClass?: string
    fieldErrorClass?: string
  }>(),
  {
    selection: 'single',
    alreadySelectedDataField: undefined,
    fieldOptions: undefined,
    fieldLabelClass: 'mb-1.5 block text-xs font-bold uppercase tracking-wider text-body',
    fieldInputClass:
      'w-full border border-stroke bg-gray-2 px-3 py-2.5 text-sm text-black outline-none focus:border-primary',
    fieldErrorClass: 'mt-1 text-sm text-red',
  },
)

const { t, te } = useI18n()

const schemaFieldOptions = computed(() => {
  if (props.fieldOptions?.length) return props.fieldOptions
  return buildSensorSchemaFieldOptions(props.sensorDetails, t, te)
})

const dataFieldOptions = computed(() => {
  const persisted = props.alreadySelectedDataField?.trim()
  if (!persisted) return schemaFieldOptions.value

  const keys = schemaFieldOptions.value.map((opt) => opt.value)
  if (keys.includes(persisted)) return schemaFieldOptions.value

  return [
    ...schemaFieldOptions.value,
    ...buildSensorSchemaFieldOptionsFromKeys([persisted], t, te),
  ]
})

const hasDataFieldOptions = computed(() => dataFieldOptions.value.length > 0)

const statusKind = computed((): WidgetDataFieldStatusKind | null => {
  if (props.isLoadingDetails) return 'loading'
  if (props.unsupportedSensorType) return 'unsupported'
  if (props.detailsError) return 'error'
  return null
})

const selectionMode = computed((): WidgetDataFieldSelectionMode => {
  if (statusKind.value) return 'status'
  if (hasDataFieldOptions.value) return props.selection
  return 'empty'
})

const formFieldName = computed(() =>
  props.selection === 'single' ? 'dataField' : 'dataFields',
)

const { value: dataFieldValue, setValue: setDataFieldValue } = useField<string>('dataField')

watch(
  [() => props.alreadySelectedDataField, selectionMode],
  ([selected, mode]) => {
    if (mode !== 'single' || !selected) return
    if (!dataFieldValue.value) {
      setDataFieldValue(selected)
    }
  },
  { immediate: true },
)

const checkboxFieldOptions = computed(() =>
  dataFieldOptions.value.map((opt) => ({
    id: opt.value,
    name: opt.label,
  })),
)

const showSchemaHint = computed(() => props.hasSelectedSensor)
</script>

<template>
  <div v-if="hasSelectedSensor" class="space-y-5">
    <div v-if="selectionMode === 'status' && statusKind === 'loading'">
      <label :class="fieldLabelClass">
        {{ t('custom_dashboards.widget_modal.data_field.label') }}
      </label>
      <p class="text-sm text-body">
        {{ t('custom_dashboards.widget_modal.data_field.loading') }}
      </p>
    </div>

    <p
      v-else-if="selectionMode === 'status' && statusKind === 'unsupported'"
      :class="fieldErrorClass"
    >
      {{ t('custom_dashboards.widget_modal.data_field.unsupported_sensor') }}
    </p>

    <p v-else-if="selectionMode === 'status' && statusKind === 'error'" :class="fieldErrorClass">
      {{ t('custom_dashboards.widget_modal.data_field.fetch_error') }}
    </p>

    <div v-else-if="selectionMode === 'multi'">
      <label :class="fieldLabelClass">
        {{ t('custom_dashboards.widget_modal.data_field.label') }}
      </label>
      <DropdownCheckbox
        name="dataFields"
        id-mode="string"
        :options="checkboxFieldOptions"
        label-attribute="name"
      />
    </div>

    <Field
      v-else-if="selectionMode === 'single-array'"
      name="dataFields"
      v-slot="{ value, handleChange, errorMessage }"
    >
      <div>
        <label :class="fieldLabelClass">
          {{ t('custom_dashboards.widget_modal.data_field.label') }}
        </label>
        <div class="relative">
          <select
            :value="Array.isArray(value) ? value[0] ?? '' : ''"
            :class="[fieldInputClass, 'appearance-none pr-10']"
            @change="
              handleChange(
                ($event.target as HTMLSelectElement).value
                  ? [($event.target as HTMLSelectElement).value]
                  : [],
              )
            "
          >
            <option value="" disabled>
              {{ t('custom_dashboards.widget_modal.data_field.placeholder') }}
            </option>
            <option v-for="opt in dataFieldOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
          <ChevronDownIcon
            class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          />
        </div>
        <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
      </div>
    </Field>

    <Field
      v-else-if="selectionMode === 'single'"
      name="dataField"
      v-slot="{ value, handleChange, errorMessage }"
    >
      <div>
        <label :class="fieldLabelClass">
          {{ t('custom_dashboards.widget_modal.data_field.label') }}
        </label>
        <div class="relative">
          <select
            :value="(value as string) || alreadySelectedDataField || ''"
            :class="[fieldInputClass, 'appearance-none pr-10']"
            @change="handleChange(($event.target as HTMLSelectElement).value)"
          >
            <option value="" disabled>
              {{ t('custom_dashboards.widget_modal.data_field.placeholder') }}
            </option>
            <option v-for="opt in dataFieldOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
          <ChevronDownIcon
            class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          />
        </div>
        <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
      </div>
    </Field>

    <Field v-else-if="selectionMode === 'empty'" :name="formFieldName" v-slot="{ errorMessage }">
      <div>
        <label :class="fieldLabelClass">
          {{ t('custom_dashboards.widget_modal.data_field.label') }}
        </label>
        <p class="text-sm text-body">
          {{ t('custom_dashboards.widget_modal.data_field.placeholder') }}
        </p>
        <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
      </div>
    </Field>

    <div v-if="showSchemaHint" class="border-l-4 border-yellow-300 bg-yellow-100 px-4 py-3">
      <p class="text-sm leading-relaxed text-body">
        {{ t('custom_dashboards.widget_modal.schema_hint') }}
      </p>
    </div>
  </div>
</template>
